/**
 * UNVLD Stripe Connect — Platform fee calculation + helpers
 *
 * Adult-Tier Stripe Connect:
 * - Discrete billing descriptor: "UNVLD Entertainment"
 * - Per-club connected accounts
 * - Platform fee deducted automatically
 */

import type { SubscriptionTier } from '@unvld/db'

// ============================================================
// PLATFORM FEES
// ============================================================

export const PLATFORM_FEES = {
  starter:      { percentage: 3.9, fixedCents: 50 },   // 3.9% + 0.50 EUR
  professional: { percentage: 2.5, fixedCents: 30 },   // 2.5% + 0.30 EUR
  enterprise:   { percentage: 1.5, fixedCents: 20 },   // 1.5% + 0.20 EUR
} as const satisfies Record<SubscriptionTier, { percentage: number; fixedCents: number }>

export function calculatePlatformFee(amountCents: number, tier: SubscriptionTier): number {
  const { percentage, fixedCents } = PLATFORM_FEES[tier]
  return Math.round(amountCents * (percentage / 100) + fixedCents)
}

// Monthly subscription prices in cents
export const SUBSCRIPTION_PRICES = {
  starter:      4900,   // 49 EUR/month
  professional: 14900,  // 149 EUR/month
  enterprise:   34900,  // 349 EUR/month
} as const satisfies Record<SubscriptionTier, number>

// Founding club discount
export function applyFoundingDiscount(priceCents: number, discountPct: number): number {
  return Math.round(priceCents * (1 - discountPct))
}

// ============================================================
// STRIPE HELPERS
// ============================================================

export function buildPaymentMetadata(params: {
  orgId: string
  eventId: string
  ticketId: string
  tierName: string
  guestType: string
}) {
  return {
    org_id: params.orgId,
    event_id: params.eventId,
    ticket_id: params.ticketId,
    tier_name: params.tierName,
    guest_type: params.guestType,
    platform: 'unvld',
  }
}

export const STRIPE_CONFIG = {
  paymentDescriptor: 'UNVLD Entertainment',
  currency: 'eur',
  paymentMethods: ['card', 'sepa_debit'],
  // Payout delays by tier
  payoutDelay: {
    starter: 7,       // T+7 days
    professional: 3,  // T+3 days
    enterprise: 1,    // T+1 day
  } as Record<SubscriptionTier, number>,
} as const

// Format cents to EUR display string
export function formatEUR(cents: number): string {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
  }).format(cents / 100)
}

// ============================================================
// CONNECT ONBOARDING URL BUILDER
// ============================================================

export function buildOnboardingParams(orgSlug: string) {
  return {
    type: 'express' as const,
    country: 'DE',
    business_type: 'company' as const,
    capabilities: {
      card_payments: { requested: true },
      transfers: { requested: true },
      sepa_debit_payments: { requested: true },
    },
    business_profile: {
      mcc: '7929', // Entertainment
      url: `https://${orgSlug}.unvld.me`,
      product_description: 'Event tickets and venue admission',
    },
    settings: {
      payments: {
        statement_descriptor: STRIPE_CONFIG.paymentDescriptor,
      },
    },
  }
}
